// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { sprintf } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.components.field.file.i18n;

@customElement( 'uo-field-input-file-async-data' ) // eslint-disable-line require-jsdoc
export class AutomatorFieldInputFileAsyncData extends LitElement {
	// Define style
	static styles = css`${unsafeCSS( styles )}`;

	/**
	 * The MIME type of the file.
	 * @type {String}
	 */
	@property( { type: String } ) accessor mime;

	/**
	 * The URL of the file.
	 * @type {String}
	 */
	@property( { type: String } ) accessor url;

	/**
	 * Renders the component's template.
	 *
	 * @return {TemplateResult} The template for the component.
	 */
	render() {
		return html`
			<div class="wrapper">
				${ until(
					this._templateFile,
					this._templatePlaceholder
				) }
			</div>
		`;
	}

	/**
	 * Returns a Promise that resolves to the appropriate template based on file type.
	 *
	 * @private
	 * @return {Promise<TemplateResult>} The template for the file data.
	 */
	get _templateFile() {
		return until(
			_uo.utility.getFileData(
				this.fileType,
				this.url
			).then( response => {
				if ( ! response ) {
					return;
				}

				if ( response.type === 'csv' ) {
					return this._templateCSV( response );
				}

				if ( response.type === 'json' ) {
					return this._templateJSON( response );
				}

				if ( response.type === 'xml' ) {
					return this._templateXML( response );
				}

				return;
			} ).catch( error => {
				console.log( error );
				return;
			} ),
			this._templateLoading
		);
	}

	/**
	 * Returns the loading placeholder template.
	 *
	 * @private
	 * @return {TemplateResult} The loading placeholder template.
	 */
	get _templateLoading() {
		return html`
			| <uo-placeholder width="70px"></uo-placeholder>
		`;
	}

	/**
	 * Generates a template containing a list of items.
	 *
	 * @private
	 * @param {Array<String>} items The list of items to display.
	 * @return {TemplateResult|undefined} The template for the items or undefined if no items.
	 */
	_templateItems( items = [] ) {
		if ( items.length === 0 ) {
			return;
		}

		return html`
			| ${ items.map(
				( item, index ) => html`${ index > 0 ? ' | ' : ''}${ item }`
			) }
		`;
	}

	/**
	 * Generates a template for displaying CSV file data.
	 *
	 * @private
	 * @param {Object} data The CSV file data.
	 * @return {TemplateResult} The template for the CSV file data.
	 */
	_templateCSV( data ) {
		const items = [];

		// Add number of rows
		if ( data.numberOfRows ) {
			items.push(
				html`${sprintf( strings.csvRows, data.numberOfRows )}`
			);
		}

		// Add headers
		if ( data.headers ) {
			items.push(
				html`${sprintf( strings.csvHeaders, data.headers.join( ', ' ) )}`
			);
		}

		return this._templateItems( items );
	}

	/**
	 * Generates a template for displaying JSON file data.
	 *
	 * @private
	 * @param {Object} data The JSON file data.
	 * @return {TemplateResult} The template for the JSON file data.
	 */
	_templateJSON( data ) {
		const items = [];

		// Add number of items
		if ( data.numberOfItems ) {
			items.push(
				html`${sprintf( strings.jsonItems, data.numberOfItems )}`
			);
		}

		// Add keys
		if ( data.keys ) {
			items.push(
				html`${sprintf( strings.jsonKeys, data.keys.join( ', ' ) )}`
			);
		}

		return this._templateItems( items );
	}

	/**
	 * Generates a template for displaying XML file data.
	 *
	 * @private
	 * @param {Object} data The XML file data.
	 * @return {TemplateResult} The template for the XML file data.
	 */
	_templateXML( data ) {
		const items = [];

		// Add number of child elements
		if ( data.numberOfChildElements ) {
			items.push(
				html`${sprintf( strings.xmlChildElements, data.numberOfChildElements )}`
			);
		}

		// Add the name of the root element
		if ( data.rootElementName ) {
			items.push(
				html`${sprintf( strings.xmlRootElement, data.rootElementName )}`
			);
		}

		return this._templateItems( items );
	}

	/**
	 * Determines the file type based on the MIME type.
	 *
	 * @private
	 * @return {String|undefined} The file type or undefined if MIME type is not recognized.
	 */
	get fileType() {
		switch ( this.mime ) {
			case 'application/json':
				return 'json';

			case 'application/xml':
			case 'text/xml':
				return 'xml';

			case 'text/csv':
				return 'csv';

			default:
				return;
		}
	}
}
